/**
 * B2BHowItWorks — Wheel-driven step card: accordion + animated product demos
 *
 * Five assessment-centric beats. Wheel/touch events on the card advance steps;
 * at the first/last step the page scrolls naturally. Sub-columns scroll their
 * own overflow before the card switches steps (via hasScrollableRoom check).
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import {
  BarChart3,
  Bot,
  Calendar,
  Check,
  Clock,
  Loader2,
  Mail,
  Mic,
  Radio,
  Send,
  Sparkles,
  Video,
} from 'lucide-react';

type StepBullet = { bold: string; rest: string };

type Accent = {
  fill: string;
  fillPast: string;
  hatch: string;
  label: string;
  pill: string;
  leftBg: string;
  leftBorder: string;
  dmGrad0: string;
  dmGrad1: string;
  dmLeft: string;
  dmSurface: string;
  dmCell: string;
};

/**
 * Per-step tints — very light 50-scale pastels (sky, emerald, violet, indigo, amber).
 * Dark mode uses the same hues at low opacity on `dmLeft`.
 */
const LAYER_COLORS: Accent[] = [
  {
    // Step 0 — sky
    fill: '#F0F9FF',
    fillPast: '#F8FCFF',
    hatch: '#BAE6FD',
    label: '#0369A1',
    pill: '#38BDF8',
    leftBg: '#F0F9FF',
    leftBorder: '#BAE6FD',
    dmGrad0: '#1a232e',
    dmGrad1: '#121920',
    dmLeft: 'rgba(14, 165, 233, 0.12)',
    dmSurface: '#24303d',
    dmCell: '#2c3a4a',
  },
  {
    // Step 1 — emerald
    fill: '#ECFDF5',
    fillPast: '#F6FEF9',
    hatch: '#A7F3D0',
    label: '#047857',
    pill: '#34D399',
    leftBg: '#ECFDF5',
    leftBorder: '#A7F3D0',
    dmGrad0: '#1a2520',
    dmGrad1: '#121a17',
    dmLeft: 'rgba(16, 185, 129, 0.12)',
    dmSurface: '#243530',
    dmCell: '#2c4038',
  },
  {
    // Step 2 — violet
    fill: '#F5F3FF',
    fillPast: '#FAF5FF',
    hatch: '#DDD6FE',
    label: '#5B21B6',
    pill: '#A78BFA',
    leftBg: '#F5F3FF',
    leftBorder: '#DDD6FE',
    dmGrad0: '#221e2e',
    dmGrad1: '#18151f',
    dmLeft: 'rgba(124, 58, 237, 0.12)',
    dmSurface: '#2e2838',
    dmCell: '#363046',
  },
  {
    // Step 3 — indigo
    fill: '#EEF2FF',
    fillPast: '#F8FAFF',
    hatch: '#C7D2FE',
    label: '#4338CA',
    pill: '#818CF8',
    leftBg: '#EEF2FF',
    leftBorder: '#C7D2FE',
    dmGrad0: '#22212e',
    dmGrad1: '#17151f',
    dmLeft: 'rgba(99, 102, 241, 0.12)',
    dmSurface: '#2a2838',
    dmCell: '#323046',
  },
  {
    // Step 4 — amber
    fill: '#FFFBEB',
    fillPast: '#FFFCF5',
    hatch: '#FDE68A',
    label: '#B45309',
    pill: '#FBBF24',
    leftBg: '#FFFBEB',
    leftBorder: '#FDE68A',
    dmGrad0: '#2e2618',
    dmGrad1: '#1f1a12',
    dmLeft: 'rgba(245, 158, 11, 0.12)',
    dmSurface: '#3d3528',
    dmCell: '#454039',
  },
];

/** Focus: candidate assessment pipeline. Bullets: first phrase + rest. */
const STEPS: {
  id: string;
  title: string;
  desc: string;
  bullets: StepBullet[];
}[] = [
  {
    id: 'engage',
    title: 'Publish & reach candidates',
    desc: 'Jobs go live on your career site and boards; sequences and one-off messages invite the right people to apply. Every applicant lands as a candidate you can assess in one place.',
    bullets: [
      { bold: 'Multi-channel publishing.', rest: 'Career site + job boards from one draft.' },
      { bold: 'AI-assisted outreach.', rest: 'Templates and personalised email at scale.' },
      { bold: 'Unified applicant record.', rest: 'Every apply ties to the role for immediate assessment.' },
    ],
  },
  {
    id: 'fitment',
    title: 'Assess fitment to the role',
    desc: 'Before anyone spends time on calls, each profile is scored against the JD and skills you set—clear fit scores, ranking, and gap flags you can trust.',
    bullets: [
      { bold: 'Role-fit matrix.', rest: 'Compare candidates side by side with explainable scores.' },
      { bold: 'Knock-outs & gaps.', rest: 'Weak fits surface before they consume recruiter time.' },
      { bold: 'Single source of truth.', rest: 'Scores and analysis stay on the application.' },
    ],
  },
  {
    id: 'schedule',
    title: 'Schedule on the calendar',
    desc: 'Interview events live on a real calendar grid: pick slots, avoid conflicts, and let candidates confirm. Reminders can follow by email when you wire notifications.',
    bullets: [
      { bold: 'Real calendar views.', rest: 'Week and day grids with holds and conflicts visible.' },
      { bold: 'One confirmed event.', rest: 'The whole team shares the same interview record.' },
      { bold: 'Less coordination overhead.', rest: 'Fewer threads just to land a time.' },
    ],
  },
  {
    id: 'agentic',
    title: 'Agentic AI interview (video & voice)',
    desc: 'Structured AI interviews use camera and microphone together—or voice-only when that fits. Candidates can respond in English, Hinglish, or Hindi; prompts and rubrics stay consistent.',
    bullets: [
      { bold: 'Video + voice.', rest: 'Live or async-style sessions with camera and mic capture.' },
      { bold: 'Multilingual.', rest: 'English, Hinglish, and Hindi conversational flows on the same req.' },
      { bold: 'Parallel at scale.', rest: 'Many sessions without manual phone screens; transcripts and scores on the record.' },
    ],
  },
  {
    id: 'assess_review',
    title: 'Assessment evidence & review',
    desc: 'Recruiters consolidate fitment, AI interview outcomes (video/voice), and notes in one view—then share a concise assessment summary with stakeholders by email when you need alignment.',
    bullets: [
      { bold: 'Evidence in one place.', rest: 'Role-fit, AI interview outcomes, and flags together.' },
      { bold: 'Judgement, not paperwork.', rest: 'This step is about assessing fit—not offers or onboarding.' },
      { bold: 'Summary email.', rest: 'Optional packet to hiring managers for fast alignment.' },
    ],
  },
];

/** Sync with Tailwind `dark` class on `<html>` (e.g. next-themes). */
function useDarkMode() {
  const [dark, setDark] = useState(false);
  useEffect(() => {
    const el = document.documentElement;
    const read = () => setDark(el.classList.contains('dark'));
    read();
    const mo = new MutationObserver(read);
    mo.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);
  return dark;
}

function useCountUp(target: number, durationMs: number, replayKey: number) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (replayKey < 0) {
      setV(0);
      return;
    }
    setV(0);
    const start = performance.now();
    let id = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / durationMs);
      setV(Math.round(p * target));
      if (p < 1) id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [target, durationMs, replayKey]);
  return v;
}

function useTypewriter(full: string, msPerChar: number, replayKey: number) {
  const [len, setLen] = useState(0);
  useEffect(() => {
    setLen(0);
    if (!full) return;
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setLen(Math.min(i, full.length));
      if (i >= full.length) window.clearInterval(id);
    }, msPerChar);
    return () => window.clearInterval(id);
  }, [full, msPerChar, replayKey]);
  return full.slice(0, len);
}

function useLoopReplay(stepIndex: number, intervalMs = 14000) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    setTick((t) => t + 1);
  }, [stepIndex]);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => window.clearInterval(id);
  }, [stepIndex, intervalMs]);
  return tick;
}

// ── Root ───────────────────────────────────────────────────────────────────────

/**
 * Walk from `target` up to (but not including) `boundary`.
 * Returns true if a scrollable ancestor still has room to scroll in the `deltaY` direction,
 * meaning the child should handle the scroll, not the card's step-switcher.
 */
function hasScrollableRoom(target: EventTarget | null, boundary: HTMLElement, deltaY: number): boolean {
  let el = target as HTMLElement | null;
  while (el && el !== boundary) {
    const oy = window.getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 2) {
      if (deltaY > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 2) return true;
      if (deltaY < 0 && el.scrollTop > 2) return true;
    }
    el = el.parentElement;
  }
  return false;
}

export function B2BHowItWorks() {
  const cardRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const stepRef   = useRef(0);
  const accumRef  = useRef(0);
  const touchY    = useRef(0);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const THRESHOLD = 40;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (hasScrollableRoom(e.target, card, e.deltaY)) return;

      const step = stepRef.current;
      if (e.deltaY < 0 && step === 0) return;
      if (e.deltaY > 0 && step === STEPS.length - 1) return;

      e.preventDefault();
      accumRef.current += e.deltaY;

      if (accumRef.current >= THRESHOLD) {
        const next = Math.min(step + Math.floor(accumRef.current / THRESHOLD), STEPS.length - 1);
        accumRef.current = accumRef.current % THRESHOLD;
        stepRef.current = next;
        setActiveStep(next);
      } else if (accumRef.current <= -THRESHOLD) {
        const prev = Math.max(step + Math.ceil(accumRef.current / THRESHOLD), 0);
        accumRef.current = accumRef.current % THRESHOLD;
        stepRef.current = prev;
        setActiveStep(prev);
      }
    };

    /** Reset accumulator when cursor leaves the card so re-entry starts clean. */
    const onMouseLeave = () => { accumRef.current = 0; };

    const onTouchStart = (e: TouchEvent) => {
      touchY.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      const dy = touchY.current - e.touches[0].clientY;
      const step = stepRef.current;
      if (hasScrollableRoom(e.target, card, dy)) return;
      if (dy < 0 && step === 0) return;
      if (dy > 0 && step === STEPS.length - 1) return;
      if (Math.abs(dy) >= 8) e.preventDefault();
    };

    const onTouchEnd = (e: TouchEvent) => {
      const step = stepRef.current;
      const dy = touchY.current - (e.changedTouches[0]?.clientY ?? touchY.current);
      if (Math.abs(dy) < 40) return;
      if (dy > 0 && step < STEPS.length - 1) {
        stepRef.current = step + 1;
        setActiveStep(step + 1);
      } else if (dy < 0 && step > 0) {
        stepRef.current = step - 1;
        setActiveStep(step - 1);
      }
    };

    card.addEventListener('wheel',      onWheel,      { passive: false });
    card.addEventListener('touchstart', onTouchStart, { passive: true });
    card.addEventListener('touchmove',  onTouchMove,  { passive: false });
    card.addEventListener('touchend',   onTouchEnd,   { passive: true });
    card.addEventListener('mouseleave', onMouseLeave, { passive: true });

    return () => {
      card.removeEventListener('wheel',      onWheel);
      card.removeEventListener('touchstart', onTouchStart);
      card.removeEventListener('touchmove',  onTouchMove);
      card.removeEventListener('touchend',   onTouchEnd);
      card.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <section id="solution" className="scroll-mt-24 bg-transparent pt-12 md:pt-16">
      <div className="px-4 pb-2 text-center md:pb-3">
        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.28em] text-slate-400 dark:text-stone-500">
          End-to-end AI hiring
        </p>
        <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-stone-50 sm:text-4xl md:text-[2.75rem]">
          How GoGrabJob works
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-base leading-relaxed text-gray-500 dark:text-stone-400">
          Publish roles, assess fitment, schedule on the calendar, run AI interviews in video or voice, and review evidence — in one calm workspace.
        </p>
      </div>

      <div className="w-full px-4 pt-1 pb-16 sm:px-8 md:px-10 md:pb-20 lg:px-12">
        <div
          ref={cardRef}
          className="mx-auto flex h-[min(66dvh,640px)] w-full max-w-[min(92rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-3xl border border-slate-200/60 bg-white shadow-xl shadow-slate-200/30 dark:border-stone-600/35 dark:bg-[#252321] dark:shadow-2xl dark:shadow-black/50 dark:ring-1 dark:ring-stone-500/10 sm:h-[min(68dvh,660px)] lg:h-[min(72dvh,700px)]"
        >
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-5 px-4 py-6 sm:px-6 lg:flex-row lg:items-stretch lg:justify-center lg:gap-8 lg:px-10 lg:py-9 xl:gap-10 xl:px-12">
            <LeftAccordion
            activeStep={activeStep}
            onStepClick={(i) => {
              stepRef.current = i;
              accumRef.current = 0;
              setActiveStep(i);
            }}
          />
            <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col items-center justify-start overflow-y-auto lg:max-w-none">
              <div className="w-full max-w-[min(100%,40rem)] py-1 xl:max-w-[min(100%,44rem)]">
                <StepDemoPanel stepIndex={activeStep} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LeftAccordion({ activeStep, onStepClick }: { activeStep: number; onStepClick: (i: number) => void }) {
  const dark = useDarkMode();
  const scaleY = activeStep / (STEPS.length - 1);

  return (
    <div className="relative flex min-h-0 w-full min-w-0 flex-1 flex-col self-stretch lg:w-[min(38%,26rem)] lg:flex-none lg:shrink-0 xl:max-w-[28rem]">
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div className="relative pb-2">
          <div className="pointer-events-none absolute bottom-3 left-[19px] top-3 w-0.5 bg-slate-100 dark:bg-stone-700/55" />
          <motion.div
            className="pointer-events-none absolute left-[19px] top-3 w-0.5 origin-top bg-slate-400 dark:bg-stone-500"
            style={{ height: 'calc(100% - 1.5rem)' }}
            animate={{ scaleY }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />

          <div className="relative space-y-0">
            {STEPS.map((step, i) => {
              const isActive = i === activeStep;
              const isPast   = i < activeStep;
              return (
                <div
                  key={step.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onStepClick(i)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onStepClick(i)}
                  className="flex cursor-pointer items-start gap-5 pb-4 focus:outline-none"
                >
                  <motion.div
                    className="relative z-10 mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-semibold tabular-nums text-slate-600 dark:text-stone-400"
                    animate={{
                      backgroundColor: isActive
                        ? (dark ? '#292524' : '#0f172a')
                        : isPast
                          ? (dark ? '#44403c' : '#f1f5f9')
                          : (dark ? '#3f3c3a' : '#ffffff'),
                      borderColor: isActive
                        ? (dark ? '#57534e' : '#0f172a')
                        : isPast
                          ? (dark ? '#57534e' : '#cbd5e1')
                          : (dark ? '#57534e' : '#e2e8f0'),
                      color: isActive ? '#ffffff' : isPast ? (dark ? '#e7e5e4' : '#475569') : (dark ? '#94a3b8' : '#94a3b8'),
                      scale: isActive ? 1.04 : 1,
                    }}
                    transition={{ duration: 0.35 }}
                  >
                    {isPast ? '\u2713' : String(i + 1).padStart(2, '0')}
                  </motion.div>

                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-[15px] font-bold leading-snug tracking-tight md:text-base ${
                        isActive
                          ? 'text-black dark:text-stone-50'
                          : isPast
                            ? 'text-slate-800 dark:text-stone-200'
                            : 'text-slate-400 dark:text-stone-500'
                      }`}
                    >
                      {step.title}
                    </p>

                    <motion.div
                      initial={false}
                      animate={{ height: isActive ? 'auto' : 0, opacity: isActive ? 1 : 0 }}
                      transition={{ duration: 0.38, ease: [0.4, 0, 0.2, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="mt-2 rounded-xl border border-slate-200/90 bg-slate-50/90 px-3 py-2.5 dark:border-stone-600/60 dark:bg-stone-800/35">
                        <p className="text-[0.875rem] font-normal leading-relaxed text-slate-700 dark:text-stone-300">
                          {step.desc}
                        </p>
                        <ul className="mt-2.5 list-disc space-y-1.5 pl-4 text-[0.8125rem] font-normal leading-relaxed text-slate-600 dark:text-stone-400">
                          {step.bullets.map((b) => (
                            <li key={b.bold} className="marker:text-slate-400 dark:marker:text-stone-500">
                              <span className="font-medium text-slate-800 dark:text-stone-200">{b.bold}</span>
                              {' '}
                              {b.rest}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Right-hand demo: one soft cool-gray wash for all steps (not tied to step accent colors). */
const DEMO_PANEL_INNER_BG_LIGHT =
  'linear-gradient(168deg, #e8edf5 0%, #f2f5f9 36%, #fafbfc 72%, #ffffff 100%)';
const DEMO_PANEL_INNER_BG_DARK =
  'linear-gradient(168deg, #232220 0%, #1c1a18 38%, #161514 100%)';

/** Assessment-review mockup only — indigo/slate accents (avoids amber/yellow). */
const REVIEW_DEMO = {
  light: {
    pill: '#4f46e5',
    label: '#312e81',
    hatch: '#a5b4fc',
    fillPast: '#eef2ff',
  },
  dark: {
    pill: '#818cf8',
    label: '#e0e7ff',
    hatch: '#6366f1',
    dmSurface: 'rgba(30, 27, 75, 0.45)',
  },
} as const;

// ── Demo panel (animated UI per step) ─────────────────────────────────────────

function StepDemoPanel({ stepIndex }: { stepIndex: number }) {
  const dark = useDarkMode();
  const c = LAYER_COLORS[stepIndex];
  const step = STEPS[stepIndex];
  const replay = useLoopReplay(stepIndex);
  const panelBg = dark ? DEMO_PANEL_INNER_BG_DARK : DEMO_PANEL_INNER_BG_LIGHT;

  return (
    <div className="relative w-full">
      <div className="rounded-2xl border border-slate-200/80 border-t-[3px] border-t-slate-300/90 bg-white shadow-lg shadow-slate-200/40 dark:border-stone-500/50 dark:border-t-stone-600 dark:bg-stone-900 dark:shadow-lg dark:shadow-black/40">
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2.5 dark:border-stone-600">
          <span className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400/90" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/90" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/90" />
          </span>
          <span className="truncate text-sm font-semibold text-slate-700 dark:text-stone-200">
            GoGrabJob · <span className="text-slate-900 dark:text-stone-50">{step.title}</span>
          </span>
        </div>

        <div
          className="min-h-[280px] p-4 antialiased sm:p-5 lg:min-h-[300px] dark:[text-rendering:geometricPrecision]"
          style={dark ? { backgroundColor: panelBg } : { background: panelBg }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={stepIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {stepIndex === 0 && <DemoEngage accent={c} replay={replay} />}
              {stepIndex === 1 && <DemoFitment accent={c} replay={replay} />}
              {stepIndex === 2 && <DemoCalendar accent={c} replay={replay} />}
              {stepIndex === 3 && <DemoAgenticInterview accent={c} replay={replay} />}
              {stepIndex === 4 && <DemoAssessmentReview replay={replay} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

const ENGAGE_SUMMARY =
  'Own the payments API, partner with product, ship weekly. Stack: React, Node, Postgres.';

function DemoEngage({ accent, replay }: { accent: Accent; replay: number }) {
  const dark = useDarkMode();
  const neutralLine = dark ? '#57534e' : '#e8ecf0';
  const typed = useTypewriter(ENGAGE_SUMMARY, 28, replay);
  const publish = useCountUp(100, 1800, replay);
  const [phase, setPhase] = useState<'draft' | 'publish' | 'live'>('draft');
  useEffect(() => {
    setPhase('draft');
    const a = window.setTimeout(() => setPhase('publish'), 400);
    const b = window.setTimeout(() => setPhase('live'), 2600);
    return () => { window.clearTimeout(a); window.clearTimeout(b); };
  }, [replay]);

  const boards = ['LinkedIn', 'Naukri', 'Indeed', '+47'] as const;

  return (
    <div className="space-y-3 text-left">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Job draft</p>
          <motion.p
            className="text-sm font-bold text-slate-800 dark:text-slate-100"
            initial={{ opacity: 0.6 }}
            animate={{ opacity: 1 }}
            transition={{ repeat: phase === 'publish' ? Infinity : 0, duration: 0.8 }}
          >
            Senior Full-Stack Engineer
          </motion.p>
        </div>
        <motion.span
          className="flex shrink-0 items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold shadow-sm ring-1 ring-slate-200/80 dark:bg-slate-800 dark:ring-slate-600"
          style={{ color: accent.label }}
          animate={{ scale: phase === 'live' ? [1, 1.06, 1] : 1 }}
          transition={{ duration: 0.5 }}
        >
          {phase === 'draft' && <Loader2 className="h-3 w-3 animate-spin" />}
          {phase === 'publish' && <Sparkles className="h-3 w-3" />}
          {phase === 'live' && <Check className="h-3 w-3" style={{ color: accent.label }} />}
          {phase === 'live' ? 'Live' : phase === 'publish' ? 'Publishing' : 'Generating'}
        </motion.span>
      </div>

      <div className="rounded-lg border border-slate-200/80 bg-white/90 p-2.5 text-sm leading-snug text-slate-600 shadow-sm dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200">
        <span className="font-semibold text-slate-700 dark:text-slate-200">AI summary · </span>
        {typed}
        <motion.span
          className="inline-block w-0.5 translate-y-px"
          style={{ backgroundColor: accent.pill }}
          animate={{ opacity: [1, 0, 1] }}
          transition={{ repeat: Infinity, duration: 0.9 }}
        >
          &nbsp;
        </motion.span>
      </div>

      {phase !== 'draft' && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-stone-400">
            <span>Distributing to boards</span>
            <span>{publish}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
            <div
              className="h-full rounded-full transition-[width] duration-75 ease-out"
              style={{ width: `${publish}%`, backgroundColor: accent.pill }}
            />
          </div>
        </div>
      )}

      <div>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Live on boards</p>
        <div className="flex flex-wrap gap-1.5">
          {boards.map((b, i) => (
            <motion.span
              key={b}
              initial={{ opacity: 0, scale: 0.85, y: 6 }}
              animate={{
                opacity: phase === 'live' && publish >= 30 + i * 20 ? 1 : phase === 'live' ? 0.4 : 0.35,
                scale: phase === 'live' && publish >= 30 + i * 20 ? 1 : 0.95,
                y: 0,
              }}
              transition={{ delay: i * 0.08, type: 'spring', stiffness: 380, damping: 22 }}
              className="rounded-md px-2 py-1 text-[10px] font-semibold"
              style={{
                background: accent.fillPast,
                color: accent.label,
                border: `1px solid ${accent.hatch}`,
              }}
            >
              {b}
            </motion.span>
          ))}
        </div>
      </div>

      {phase === 'live' && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden rounded-lg border border-slate-200/90 bg-white shadow-sm dark:border-stone-600 dark:bg-stone-800"
        >
          <div
            className="flex items-center gap-2 border-b px-2.5 py-1.5"
            style={{ backgroundColor: accent.fillPast, borderColor: accent.hatch }}
          >
            <Mail className="h-3.5 w-3.5 shrink-0 opacity-80" style={{ color: accent.label }} />
            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300">Simulated outreach email</span>
          </div>
          <div className="space-y-2 p-3 text-sm leading-snug text-slate-700 dark:text-stone-200">
            <p><span className="font-semibold text-slate-600 dark:text-stone-400">From:</span> careers@yourcompany.com</p>
            <p><span className="font-semibold text-slate-600 dark:text-stone-400">To:</span> candidate@email.com</p>
            <p><span className="font-semibold text-slate-600 dark:text-stone-400">Subject:</span> Full-stack role — quick intro from our team</p>
            <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-600 dark:bg-stone-900/90 dark:text-stone-300">
              Hi — we liked your backend work. Here is a short overview of the stack and how we assess fit. Reply if you would like to move forward.
            </p>
            <div className="flex items-center justify-between pt-0.5">
              <span className="text-xs font-semibold text-slate-500 dark:text-stone-500">Template · Sequence A</span>
              <motion.span
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold text-white"
                style={{ backgroundColor: accent.pill }}
                animate={{ opacity: [1, 0.75, 1] }}
                transition={{ repeat: Infinity, duration: 1.4 }}
              >
                <Send className="h-2.5 w-2.5" /> Sending…
              </motion.span>
            </div>
          </div>
        </motion.div>
      )}

      <motion.div
        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/90 px-2.5 py-2 dark:border-slate-600 dark:bg-slate-800/40"
        animate={phase === 'live' ? { borderColor: [accent.hatch, neutralLine, accent.hatch] } : {}}
        transition={{ duration: 2, repeat: phase === 'live' ? Infinity : 0 }}
      >
        <Bot className="h-4 w-4 shrink-0" style={{ color: accent.label }} />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Outreach queue</p>
          <p className="truncate text-[11px] text-slate-700 dark:text-slate-200">
            <motion.span
              key={replay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {phase === 'live' ? 'Personalised invites in flight…' : 'Queued'}
            </motion.span>
          </p>
        </div>
        <motion.div
          animate={phase === 'live' ? { x: [0, 3, 0] } : {}}
          transition={{ repeat: Infinity, duration: 1.2 }}
        >
          <Send className="h-3.5 w-3.5" style={{ color: accent.label }} />
        </motion.div>
      </motion.div>
    </div>
  );
}

function DemoAgenticInterview({ accent, replay }: { accent: Accent; replay: number }) {
  const [row0, setRow0] = useState<'live' | 'done'>('live');
  useEffect(() => {
    setRow0('live');
    const id = window.setTimeout(() => setRow0('done'), 2200);
    return () => window.clearTimeout(id);
  }, [replay]);

  const doneKey = row0 === 'done' ? replay : -1;
  const sKapoor = useCountUp(82, 850, doneKey);
  const sMenon = useCountUp(84, 850, doneKey >= 0 ? doneKey + 1 : -1);
  const sPatel = useCountUp(76, 850, doneKey >= 0 ? doneKey + 2 : -1);

  const langs = ['English', 'Hinglish', 'Hindi'] as const;

  return (
    <div className="space-y-3 text-left">
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className="rounded-md px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white"
          style={{ backgroundColor: accent.pill }}
        >
          Video
        </span>
        <span
          className="rounded-md px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide"
          style={{ background: accent.fillPast, color: accent.label, border: `1px solid ${accent.hatch}` }}
        >
          Voice
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">· modes</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Languages</p>
        <div className="flex flex-wrap gap-1">
          {langs.map((L) => (
            <span
              key={L}
              className="rounded-md px-1.5 py-0.5 text-[9px] font-bold"
              style={{ background: accent.fillPast, color: accent.label, border: `1px solid ${accent.hatch}` }}
            >
              {L}
            </span>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200/80 bg-slate-900/95 shadow-sm dark:border-slate-600">
        <div className="relative aspect-video max-h-[104px]">
          <div
            className="absolute inset-0 opacity-90"
            style={{ background: `linear-gradient(145deg, ${accent.label}33 0%, #1e293b 45%, #0f172a 100%)` }}
          />
          <Video className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2 text-white/35" strokeWidth={1.25} />
          <motion.span
            className="absolute bottom-1.5 right-1.5 rounded bg-red-600/95 px-1.5 py-0.5 text-[8px] font-bold text-white"
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ repeat: row0 === 'live' ? Infinity : 0, duration: 1 }}
          >
            REC
          </motion.span>
          <p className="absolute bottom-1.5 left-2 text-[8px] font-semibold text-white/80">Camera + mic · structured AI interview</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Live sessions</p>
        <motion.span
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold"
          style={{ background: accent.fillPast, color: accent.label }}
          animate={{ opacity: [1, 0.72, 1] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <Radio className="h-3 w-3" />
          12 active
        </motion.span>
      </div>

      <motion.div
        className="flex items-center gap-3 rounded-lg border border-slate-200/80 bg-white p-2.5 shadow-sm dark:border-slate-600 dark:bg-slate-800/50"
        animate={row0 === 'live' ? { boxShadow: [`0 0 0 0 ${accent.pill}33`, `0 0 0 4px ${accent.pill}22`, `0 0 0 0 ${accent.pill}33`] } : {}}
        transition={{ repeat: row0 === 'live' ? Infinity : 0, duration: 1.2 }}
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          A
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-slate-800 dark:text-slate-100">A. Kapoor</p>
          <p className="text-[9px] font-semibold text-slate-500">English · video + voice</p>
          <div className="flex flex-wrap items-center gap-2">
            {row0 === 'live' ? (
              <>
                <span className="flex items-center gap-0.5 text-[9px] font-bold text-slate-500">
                  <Video className="h-3 w-3" style={{ color: accent.pill }} /> Live
                </span>
                <Mic className="h-3 w-3 shrink-0" style={{ color: accent.pill }} />
                <VoiceBars accent={accent} />
              </>
            ) : (
              <p className="flex items-center gap-1 text-[10px]" style={{ color: accent.label }}>
                <Check className="h-3 w-3" /> Interview complete
              </p>
            )}
          </div>
        </div>
        <span className="shrink-0 text-xs font-extrabold tabular-nums" style={{ color: row0 === 'live' ? accent.pill : accent.label }}>
          {row0 === 'live' ? '···' : sKapoor}
        </span>
      </motion.div>

      {[
        { n: 'R. Menon', score: sMenon, lang: 'Hinglish', mode: 'Voice-first' },
        { n: 'S. Patel', score: sPatel, lang: 'Hindi', mode: 'Video + voice' },
      ].map((row, idx) => (
        <motion.div
          key={row.n}
          initial={{ opacity: 0.4, x: -6 }}
          animate={{ opacity: row0 === 'done' ? 1 : 0.45, x: 0 }}
          transition={{ delay: idx * 0.15 }}
          className="flex items-center gap-3 rounded-lg border border-slate-200/80 bg-white p-2.5 shadow-sm dark:border-slate-600 dark:bg-slate-800/50"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {row.n[0]}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-slate-800 dark:text-slate-100">{row.n}</p>
            <p className="flex flex-wrap items-center gap-x-1 text-[10px] text-slate-500">
              <span className="font-semibold text-slate-600 dark:text-slate-400">{row.mode}</span>
              <span>·</span>
              <span>{row.lang}</span>
            </p>
          </div>
          <span className="shrink-0 text-xs font-extrabold tabular-nums" style={{ color: accent.label }}>
            {row0 === 'done' ? row.score : '—'}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

function VoiceBars({ accent }: { accent: Accent }) {
  return (
    <div className="flex h-4 items-end gap-0.5">
      {[0.35, 0.7, 1, 0.55, 0.85].map((m, i) => (
        <motion.div
          key={i}
          className="w-0.5 rounded-sm opacity-85"
          style={{ backgroundColor: accent.pill }}
          animate={{ height: [4, 14 * m, 6, 12 * m, 4] }}
          transition={{ repeat: Infinity, duration: 0.9, delay: i * 0.08, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

function DemoFitment({ accent, replay }: { accent: Accent; replay: number }) {
  const n1 = useCountUp(91, 1100, replay);
  const n2 = useCountUp(84, 1200, replay + 1);
  const n3 = useCountUp(71, 1300, replay + 2);
  const scores = [n1, n2, n3];

  return (
    <div className="space-y-3 text-left">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Fitment vs job</p>
        <motion.div animate={{ rotate: [0, 8, -8, 0] }} transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}>
          <BarChart3 className="h-4 w-4" style={{ color: accent.pill }} />
        </motion.div>
      </div>
      <p className="text-[10px] text-slate-500 dark:text-slate-400">
        Senior Full-Stack Engineer · JD + required skills
      </p>
      <div className="overflow-hidden rounded-lg border border-slate-200/80 bg-white shadow-sm dark:border-stone-600 dark:bg-stone-800">
        <div className="grid grid-cols-4 gap-0 border-b border-slate-100 bg-slate-50/90 text-xs font-bold uppercase tracking-tight text-slate-600 dark:border-stone-600 dark:bg-stone-800/90 dark:text-stone-300">
          <div className="p-2">Candidate</div>
          <div className="p-2 text-center">Fit</div>
          <div className="p-2 text-center">Pool</div>
          <div className="p-2 text-center">Gaps</div>
        </div>
        {[
          ['M. Shah', 'Top 5%', '1'],
          ['J. Lee', 'Top 12%', '2'],
          ['K. Wu', '—', '4'],
        ].map(([name, bulk, gaps], ri) => (
          <motion.div
            key={name}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: ri * 0.12 + 0.05, type: 'spring', stiffness: 300, damping: 24 }}
            className="grid grid-cols-4 border-b border-slate-50 text-sm last:border-0 dark:border-stone-700/60"
          >
            <div className="p-2 font-semibold text-slate-800 dark:text-stone-100">{name}</div>
            <div className="p-2 text-center font-bold tabular-nums" style={{ color: accent.label }}>{scores[ri]}</div>
            <div className="p-2 text-center text-slate-700 dark:text-stone-300">{bulk}</div>
            <div className="p-2 text-center text-slate-600 dark:text-stone-400">{gaps}</div>
          </motion.div>
        ))}
      </div>
      <motion.p
        className="text-[10px] leading-relaxed text-slate-500 dark:text-slate-400"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        Gap analysis syncs to the application before scheduling…
      </motion.p>
    </div>
  );
}

/** Mini month grid + day timeline to simulate scheduler / calendar (not a real date picker). */
function DemoCalendar({ accent, replay }: { accent: Accent; replay: number }) {
  const dark = useDarkMode();
  const idleBorder = dark ? '#57534e' : '#e8ecf0';
  const idleBg = dark ? accent.dmSurface : '#fafbfc';
  const [highlight, setHighlight] = useState(0);
  useEffect(() => {
    setHighlight(0);
    const a = window.setTimeout(() => setHighlight(1), 700);
    const b = window.setTimeout(() => setHighlight(2), 2000);
    return () => { window.clearTimeout(a); window.clearTimeout(b); };
  }, [replay]);

  const week = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
  const days = [12, 13, 14, 15, 16, 17, 18] as const;

  const blocks = [
    { t: '10:00', label: 'AI interview (video) · M. Shah', active: highlight >= 1 },
    { t: '14:30', label: 'HM conversation · panel link', active: highlight >= 2 },
  ] as const;

  return (
    <div className="space-y-3 text-left">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Calendar simulation</p>
          <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">April 2026 · Recruiting</p>
        </div>
        <Calendar className="h-4 w-4 opacity-80" style={{ color: accent.label }} />
      </div>

      <div className="rounded-lg border border-slate-200/80 bg-white p-2 shadow-sm dark:border-slate-600 dark:bg-slate-800/50">
        <div className="grid grid-cols-7 gap-0.5 text-center text-[8px] font-bold uppercase text-slate-400 dark:text-slate-500">
          {week.map((d, wi) => (
            <div key={`${wi}-${d}`} className="py-0.5">{d}</div>
          ))}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-0.5">
          {days.map((d) => {
            const isFocus = d === 15;
            return (
              <div
                key={d}
                className="flex aspect-square items-center justify-center rounded-md text-[10px] font-semibold text-slate-600 dark:text-slate-300"
                style={
                  isFocus
                    ? { backgroundColor: accent.fillPast, color: accent.label, boxShadow: `inset 0 0 0 1.5px ${accent.hatch}` }
                    : { backgroundColor: dark ? accent.dmCell : '#f8fafc' }
                }
              >
                {d}
              </div>
            );
          })}
        </div>
        <p className="mt-1.5 text-center text-[9px] text-slate-400 dark:text-slate-500">15 selected · 3 events this week</p>
      </div>

      <div>
        <p className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          <Clock className="h-3 w-3" /> Tue 15 · day view
        </p>
        <div className="space-y-1.5">
          {blocks.map((b) => (
            <motion.div
              key={b.t}
              className="flex items-start gap-2 rounded-lg border px-2.5 py-2 text-[10px] shadow-sm dark:border-slate-600"
              style={
                b.active
                  ? { borderColor: accent.hatch, backgroundColor: accent.fillPast }
                  : { borderColor: idleBorder, backgroundColor: idleBg }
              }
              animate={b.active && b.t === '14:30' ? { borderColor: [accent.hatch, idleBorder, accent.hatch] } : {}}
              transition={{ duration: 1.3, repeat: b.active && b.t === '14:30' ? Infinity : 0 }}
            >
              <span className="w-10 shrink-0 font-extrabold tabular-nums text-slate-500">{b.t}</span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800 dark:text-slate-100">{b.label}</p>
                <p className="text-[9px] text-slate-500 dark:text-slate-400">Invite + ICS · tentative until candidate confirms</p>
              </div>
              {b.active && (
                <Check className="h-3.5 w-3.5 shrink-0 opacity-70" style={{ color: accent.label }} />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DemoAssessmentReview({ replay }: { replay: number }) {
  const dark = useDarkMode();
  const a = dark ? REVIEW_DEMO.dark : REVIEW_DEMO.light;
  const fit = useCountUp(91, 900, replay);
  const voice = useCountUp(84, 1000, replay + 1);
  const [mailPhase, setMailPhase] = useState<'idle' | 'sending' | 'sent'>('idle');
  useEffect(() => {
    setMailPhase('idle');
    const t1 = window.setTimeout(() => setMailPhase('sending'), 1200);
    const t2 = window.setTimeout(() => setMailPhase('sent'), 2600);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [replay]);

  const rubric = [
    ['Role fit', 91],
    ['AI interview', 84],
    ['Consistency', 88],
  ] as const;

  const scoreCardStyle = dark
    ? { backgroundColor: a.dmSurface, border: `1px solid ${a.hatch}` as const }
    : { background: a.fillPast, border: `1px solid ${a.hatch}` as const };
  const scoreLabelClass = dark ? 'text-stone-400' : 'text-slate-500';

  return (
    <div className="space-y-4 text-left">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-stone-400">Assessment workspace</p>
        <BarChart3 className="h-5 w-5 shrink-0 opacity-90" style={{ color: a.pill }} />
      </div>
      <p className="text-sm font-semibold leading-snug text-slate-800 dark:text-stone-100">M. Shah · Senior Full-Stack Engineer</p>

      <div className="flex gap-3">
        <div className="flex-1 rounded-xl px-3 py-2.5 text-center shadow-sm dark:shadow-none" style={scoreCardStyle}>
          <p className={`text-xs font-bold uppercase tracking-wide ${scoreLabelClass}`}>Fitment</p>
          <p className="text-2xl font-extrabold tabular-nums" style={{ color: a.label }}>
            {fit}
          </p>
        </div>
        <div className="flex-1 rounded-xl px-3 py-2.5 text-center shadow-sm dark:shadow-none" style={scoreCardStyle}>
          <p className={`text-xs font-bold uppercase tracking-wide ${scoreLabelClass}`}>Interview</p>
          <p className="text-2xl font-extrabold tabular-nums" style={{ color: a.label }}>
            {voice}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {rubric.map(([label, pct], i) => (
          <div key={label} className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-xs font-semibold text-slate-700 dark:text-stone-300">{label}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-stone-700">
              <motion.div
                key={`${replay}-${label}`}
                className="h-full rounded-full"
                style={{ backgroundColor: a.pill }}
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.55, delay: 0.2 + i * 0.1, ease: 'easeOut' }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-stone-600 dark:bg-stone-800">
        <div
          className="flex items-center gap-2 border-b px-3 py-2 dark:border-stone-600"
          style={
            dark
              ? { backgroundColor: a.dmSurface, borderColor: a.hatch }
              : { backgroundColor: a.fillPast, borderColor: a.hatch }
          }
        >
          <Mail className="h-4 w-4 shrink-0" style={{ color: a.pill }} />
          <span className="text-xs font-bold text-slate-700 dark:text-stone-200">Simulated summary email</span>
        </div>
        <div className="space-y-2 p-3 text-sm leading-snug text-slate-700 dark:text-stone-200">
          <p><span className="font-semibold text-slate-600 dark:text-stone-400">To:</span>{' '}hiring.manager@yourcompany.com</p>
          <p><span className="font-semibold text-slate-600 dark:text-stone-400">Subject:</span>{' '}Assessment packet · M. Shah · Full-Stack</p>
          <p className="rounded-lg bg-slate-50 p-2.5 text-xs leading-relaxed text-slate-600 dark:bg-stone-900/80 dark:text-stone-300">
            Summary: strong role fit (91), AI interview (video + voice) clear and structured (84). Two minor gaps on infra depth — see rubric. Recommend moving to your conversation slot on Tue 15.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
            <span className="text-xs text-slate-500 dark:text-stone-500">For alignment only · not an offer step</span>
            <motion.span
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-bold text-white"
              style={{ backgroundColor: a.pill }}
              animate={mailPhase === 'sending' ? { opacity: [1, 0.65, 1] } : {}}
              transition={{ repeat: mailPhase === 'sending' ? Infinity : 0, duration: 0.9 }}
            >
              {mailPhase === 'idle' && 'Ready'}
              {mailPhase === 'sending' && (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…
                </>
              )}
              {mailPhase === 'sent' && (
                <>
                  <Check className="h-3.5 w-3.5" /> Sent
                </>
              )}
            </motion.span>
          </div>
        </div>
      </div>
    </div>
  );
}
